import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';

export default function PublicProfile() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/accounts/member/${slug}/`)
      .then(res => setProfile(res.data))
      .catch(() => setError('Member profile not found.'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0B5D1E]/20 border-t-[#0B5D1E]"></div>
    </div>
  );

  if (error) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
      <svg className="w-20 h-20 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
      <p className="text-xl text-gray-500 mb-4">{error}</p>
      <Link to="/find-surveyor" className="text-[#0B5D1E] hover:text-[#C8A951] font-semibold transition">
        &larr; Back to Directory
      </Link>
    </div>
  );

  return (
    <div>
      {/* Hero Banner */}
      <div className="relative bg-[#0B5D1E] py-16 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: "url('/Surveyor003.jpeg')" }}></div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          {profile.photo ? (
            <img
              src={profile.photo}
              alt={profile.full_name}
              className="w-32 h-32 rounded-full mx-auto border-4 border-[#C8A951]/60 object-cover shadow-xl"
            />
          ) : (
            <div className="w-32 h-32 rounded-full mx-auto border-4 border-[#C8A951]/60 bg-white/10 flex items-center justify-center text-5xl text-white font-bold shadow-xl" style={{ fontFamily: "'Playfair Display', serif" }}>
              {profile.full_name?.charAt(0)}
            </div>
          )}
          <h1 className="text-3xl md:text-4xl font-bold text-white mt-5" style={{ fontFamily: "'Playfair Display', serif" }}>
            {profile.full_name}
          </h1>
          <p className="text-[#C8A951] text-lg font-medium mt-2">{profile.membership_category}</p>
          <span className="inline-block mt-3 bg-white/15 text-white text-sm px-5 py-1.5 rounded-full border border-white/20">
            {profile.membership_number}
          </span>
        </div>
      </div>

      {/* Profile Details */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {profile.bio && (
            <div className="px-8 pt-8 pb-0">
              <h3 className="text-xs font-bold text-[#0B5D1E] uppercase tracking-widest mb-2 flex items-center gap-2">
                <span className="w-5 h-[2px] bg-[#C8A951]"></span>
                About
              </h3>
              <p className="text-gray-700 leading-relaxed">{profile.bio}</p>
            </div>
          )}

          <div className="px-8 py-8">
            <h3 className="text-xs font-bold text-[#0B5D1E] uppercase tracking-widest mb-5 flex items-center gap-2">
              <span className="w-5 h-[2px] bg-[#C8A951]"></span>
              Professional Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {profile.email && (
                <div className="flex items-start gap-3 p-4 bg-[#F8FAFB] rounded-xl">
                  <svg className="w-5 h-5 text-[#0B5D1E] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Email</p>
                    <p className="text-gray-800 font-medium">{profile.email}</p>
                  </div>
                </div>
              )}
              {profile.phone_number && (
                <div className="flex items-start gap-3 p-4 bg-[#F8FAFB] rounded-xl">
                  <svg className="w-5 h-5 text-[#0B5D1E] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                  </svg>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Phone</p>
                    <p className="text-gray-800 font-medium">{profile.phone_number}</p>
                  </div>
                </div>
              )}
              {profile.firm_name && (
                <div className="flex items-start gap-3 p-4 bg-[#F8FAFB] rounded-xl">
                  <svg className="w-5 h-5 text-[#0B5D1E] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                  </svg>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Firm</p>
                    <p className="text-gray-800 font-medium">{profile.firm_name}</p>
                  </div>
                </div>
              )}
              {profile.specialization && (
                <div className="flex items-start gap-3 p-4 bg-[#F8FAFB] rounded-xl">
                  <svg className="w-5 h-5 text-[#0B5D1E] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                  </svg>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Specialization</p>
                    <p className="text-gray-800 font-medium">{profile.specialization}</p>
                  </div>
                </div>
              )}
              {profile.surcon_number && (
                <div className="flex items-start gap-3 p-4 bg-[#F8FAFB] rounded-xl">
                  <svg className="w-5 h-5 text-[#0B5D1E] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"/>
                  </svg>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">SURCON Number</p>
                    <p className="text-gray-800 font-medium">{profile.surcon_number}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="px-8 pb-8 flex items-center justify-between">
            <span className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold ${
              profile.membership_status === 'active'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-gray-100 text-gray-600 border border-gray-200'
            }`}>
              <span className={`w-2 h-2 rounded-full ${profile.membership_status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
              {profile.membership_status === 'active' ? 'Active Member' : profile.membership_status}
            </span>
            <Link to="/find-surveyor" className="text-[#0B5D1E] hover:text-[#C8A951] text-sm font-semibold transition flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg>
              Back to Directory
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
